// src/features/quality/pages/QualityCheckRevisionDetailPage.tsx
// AM-008: adds an edit form for draft + not_required revisions.

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthStore } from "@/auth/authStore";
import { usePermission } from "@/auth/usePermission";
import { RevisionStatusBadge } from "../components/RevisionStatusBadge";
import { SubmitForApprovalDialog } from "../components/SubmitForApprovalDialog";

import {
  useV1QualityCheckRevisionsRetrieve,
  useV1QualityCheckRevisionsSubmitForApprovalCreate,
  useV1QualityCheckRevisionsPartialUpdate,
} from "@/api/generated/v1/v1";
import { CustomFieldsPanel } from "@/features/customFields/CustomFieldsPanel";
import { PrintActionDialog } from "@/shared/components/printing/PrintActionDialog";
import { PageBanner } from "@/shared/components/PageBanner";

export function QualityCheckRevisionDetailPage() {
  const { revisionId } = useParams<{ revisionId: string }>();
  const navigate = useNavigate();
  const canManage = usePermission("quality.manage");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const user = useAuthStore((s) => s.user);
  const [editIsActive, setEditIsActive] = useState(true);
  const [editError, setEditError] = useState<string | null>(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  const { data, isLoading, refetch } = useV1QualityCheckRevisionsRetrieve(
    revisionId!
  );
  const submitMutation = useV1QualityCheckRevisionsSubmitForApprovalCreate();
  const updateMutation = useV1QualityCheckRevisionsPartialUpdate();

  const revision = data;

  if (isLoading) return <p style={{ padding: 24 }}>در حال بارگذاری...</p>;
  if (!revision) return <p style={{ padding: 24 }}>یافت نشد.</p>;

  const canSubmit =
    canManage &&
    revision.status === "draft" &&
    revision.approval_state === "not_required";

  // AM-008 Rule QC-08-02: same gate the backend enforces — draft AND
  // not_required, not just draft. A revision sitting at approval_state
  // "pending" is still status=draft but must not be editable.
  const canEdit = canManage && canSubmit;

  function startEditing() {
    setEditName(revision!.name);
    setEditDescription(revision!.description ?? "");
    setEditIsActive(revision!.is_active ?? false);
    setEditError(null);
    setEditing(true);
  }

  async function saveEdit() {
    setEditError(null);
    if (!editName.trim()) {
      setEditError("نام نمی‌تواند خالی باشد.");
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: revisionId!,
        data: {
          name: editName,
          description: editDescription,
          is_active: editIsActive,
        },
      });
      setEditing(false);
      refetch();
    } catch (err: any) {
      setEditError(
        err?.response?.data?.non_field_errors?.[0] ??
          err?.response?.data?.name?.[0] ??
          "خطا در ذخیرهٔ تغییرات."
      );
    }
  }

  async function handleSubmit(workflowRevisionId: string) {
    await submitMutation.mutateAsync({
      id: revisionId!,
      data: { workflow_revision_id: workflowRevisionId },
    });
    setDialogOpen(false);
    refetch();
  }

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title={`Rev ${revision.revision_no} ${!editing ? `— ${revision.name}` : ""}`}
        subtitle="جزئیات و تغییرات این تجدیدنظر کیفی"
        icon="📄"
        helpLink="/help/quality/quality-check-revision-detail"
      >
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <button
            onClick={() => setPrintDialogOpen(true)}
            style={{
              padding: "0.6rem 1.25rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
              transition: "all 0.2s ease"
            }}
          >
            🖨️ چاپ فرم
          </button>
          <RevisionStatusBadge
            status={revision.status as any}
            approvalState={revision.approval_state as any}
          />
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>

      {printDialogOpen && (
        <PrintActionDialog
          reportType="quality.qualitycheckrevision"
          data={{
            document_number: ((revision as any)?.family_code || "") + "-" + revision.revision_no,
            product_name: revision.name,
            inspector: user?.username || "ناشناس" // Pass whatever context is available
          }}
          onClose={() => setPrintDialogOpen(false)}
        />
      )}

      {editing ? (
        <div
          style={{
            marginTop: 16,
            padding: 16,
            borderRadius: 8,
            background: "rgba(255,255,255,0.05)",
          }}
        >
          <label style={{ display: "block", marginBottom: 12 }}>
            نام
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              style={{
                display: "block",
                width: "100%",
                marginTop: 4,
                padding: 8,
                borderRadius: 4,
                background: "rgba(255,255,255,0.05)",
                color: "var(--color-text)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            />
          </label>
          <label style={{ display: "block", marginBottom: 12 }}>
            توضیحات
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              rows={3}
              style={{
                display: "block",
                width: "100%",
                marginTop: 4,
                padding: 8,
                borderRadius: 4,
                background: "rgba(255,255,255,0.05)",
                color: "var(--color-text)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={editIsActive}
              onChange={(e) => setEditIsActive(e.target.checked)}
            />
            فعال
          </label>
          {editError && (
            <p style={{ color: "#ef4444", fontSize: 13 }}>{editError}</p>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={saveEdit} disabled={updateMutation.isPending}>
              ذخیره
            </button>
            <button onClick={() => setEditing(false)}>انصراف</button>
          </div>
        </div>
      ) : (
        revision.description && (
          <p style={{ marginTop: 16, opacity: 0.8 }}>{revision.description}</p>
        )
      )}

      {!editing && (
        <div style={{ marginTop: 24, display: "flex", gap: 8 }}>
          {canEdit && <button onClick={startEditing}>ویرایش</button>}
          {canSubmit && (
            <button onClick={() => setDialogOpen(true)}>
              ارسال برای تأیید
            </button>
          )}
        </div>
      )}

      {/* AM-008: explain why edit is hidden when pending, instead of the
          button just silently not being there. */}
      {!editing && canManage && revision.status === "draft" &&
        revision.approval_state === "pending" && (
          <p style={{ marginTop: 16, opacity: 0.7, fontSize: 14 }}>
            این Revision در انتظار تأیید است — قابل ویرایش نیست.
          </p>
        )}

      {revision.approval_state === "rejected" && canManage && (
        <div style={{ marginTop: 24 }}>
          <p style={{ opacity: 0.7, fontSize: 14 }}>
            این Revision رد شده است. بر اساس ضوابط کیفی، Revision رد‌شده
            قابل ویرایش نیست — باید یک Revision جدید بسازید.
          </p>
          <button
            onClick={() =>
              navigate(`/quality/families/${revision.family}/revisions/new`)
            }
          >
            ساخت Revision جدید
          </button>
        </div>
      )}

      <div style={{ marginTop: 24, marginBottom: 24 }}>
        <CustomFieldsPanel
          targetContentType="quality.qualitycheckrevision"
          targetObjectId={revisionId!}
          editable={canEdit}
        />
      </div>

      <div style={{ marginTop: 32 }}>
        <button
          onClick={() =>
            navigate(`/quality/inspections/new?revision=${revisionId}`)
          }
        >
          ثبت بازرسی جدید برای این Revision
        </button>
      </div>

      {dialogOpen && (
        <SubmitForApprovalDialog
          isSubmitting={submitMutation.isPending}
          onSubmit={handleSubmit}
          onClose={() => setDialogOpen(false)}
        />
      )}
      </div>
    </div>
  );
}
